import { BareButton } from '@calendly/ui/components/bare-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useRef, forwardRef, useState, useLayoutEffect } from 'react';
import { useLeftNavigationState, useTextWidth } from './hooks';
import { useExpandPhaseContext } from './left_navigation_context';
const baseContainerStatePhoneCss = "b1ngakwk";
const baseContainerStatePhoneExpandedCss = "bnlgrdc";
const baseContainerStateDefaultCss = "bggvv52";
const baseContainerStateMinimizedCss = "bukyv0f";
const baseContainerStateExpandedCss = "b1hi1c7l";
const baseContainerCss = "bx30w11";

/**
 * Root container for the left nav; width and transition are driven by state.
 * @param {Object} props
 * @param {string} [props.state] - Gives CSS state to control width.
 */
export const BaseContainer = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(baseContainerCss, {
    [baseContainerStatePhoneCss]: state?.toString() === 'phone',
    [baseContainerStatePhoneExpandedCss]: state?.toString() === 'phone-expanded',
    [baseContainerStateDefaultCss]: state?.toString() === 'default',
    [baseContainerStateMinimizedCss]: state?.toString() === 'minimized',
    [baseContainerStateExpandedCss]: state?.toString() === 'expanded'
  }, props.className)} />;
});
const navigationIconContainerCss = "ntces65";
export const NavigationIconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationIconContainerCss, props.className)} />;
});
const navigationLabelCss = "n2wybsz";
export const NavigationLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(navigationLabelCss, props.className)} />;
});
const titleCss = "t1piqhvi";
export const Title = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleCss, props.className)} />;
});
const subtitleCss = "suivsuk";
export const Subtitle = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subtitleCss, props.className)} />;
});
const wrapperStatePhoneCss = "w1xxx3t6";
const wrapperStateDefaultCss = "w19l5nla";
const wrapperStateMinimizedCss = "w1bi7z02";
const wrapperStateExpandedCss = "w1eelh80";
const wrapperStatePhoneExpandedCss = "w1av3mtd";
const wrapperCss = "wgzghvc";
export const Wrapper = forwardRef((props, ref) => {
  const {
    state,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'nav';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, {
    [wrapperStatePhoneCss]: state?.toString() === 'phone',
    [wrapperStateDefaultCss]: state?.toString() === 'default',
    [wrapperStateMinimizedCss]: state?.toString() === 'minimized',
    [wrapperStateExpandedCss]: state?.toString() === 'expanded',
    [wrapperStatePhoneExpandedCss]: state?.toString() === 'phone-expanded'
  }, props.className)} />;
});
const overlayNoBackgroundCss = "o1k81ni1";
const overlayCss = "ojrz821";
export const Overlay = forwardRef((props, ref) => {
  const {
    noBackground,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(overlayCss, {
    [overlayNoBackgroundCss]: noBackground
  }, props.className)} />;
});
const topWrapperCss = "tcx3egh";

/** Top bar padding when nav is minimized (collapsed). */
export const topWrapperMinimizedCss = "t5b6vy6";

/** During collapse: keep logo left-aligned with consistent padding so the "C" doesn’t jump. */
export const topWrapperCollapsingCss = "tjfsgpv";
export const TopWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topWrapperCss, props.className)} />;
});
const toggleButtonIsPhoneCss = "t1l0y0jt";
const toggleButtonIsRelatedPositioningCss = "t2epqcz";
const toggleButtonCss = "t14lgt0q";
export const ToggleButton = forwardRef((props, ref) => {
  const {
    isRelatedPositioning,
    ...restProps
  } = props;
  const isPhone = useDeviceType('isPhone');
  return <BareButton ref={ref} {...restProps} className={classNames(toggleButtonCss, {
    [toggleButtonIsPhoneCss]: isPhone,
    [toggleButtonIsRelatedPositioningCss]: isRelatedPositioning
  }, props.className)} />;
});
const globalCreateWrapperCss = "g1jztoua";
export const globalCreateWrapperMinimizedCss = "gz91nwe";

/** Wrapper around the Create button; use globalCreateWrapperMinimizedCss when nav is minimized. */
export const GlobalCreateWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(globalCreateWrapperCss, props.className)} />;
});
const navigationItemsCss = "n10a98hy";
export const NavigationItems = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationItemsCss, props.className)} />;
});
const navigationAnchorItemBaseIsMultilineCss = "n13u6av1";
const navigationAnchorItemBaseIsMinimizedCss = "n1vb3wl";
const navigationAnchorItemBaseIsActiveCss = "n1lmldd3";
const navigationAnchorItemBaseIsCollapsingCss = "ng5lpu0";
const navigationAnchorItemBaseIsMinimizedLayoutCss = "nz5m5pm";
const navigationAnchorItemBaseIsExpandingFadeOutCss = "n1fqmonv";
const navigationAnchorItemBaseIsExpandingFadeInPendingCss = "nstuhj3";
const navigationAnchorItemBaseIsExpandingFadeInCss = "nd6zm0x";
export const navigationLabelCaptionMinimizedCss = "n1ao7m2y";

/* Shared fade-in: 250ms hold then 50ms fade (300ms total). Delay baked into keyframes so expand/collapse match. */

/* Nav item spec: margin + padding give spacing; 48px min-height, 8px gap inside item */
const navigationAnchorItemBaseCss = "n6xl302";

/** Delay (ms) before nav collapses; items fade out at full width, then width and minimized layout apply. */
export const COLLAPSE_FADEOUT_MS = 100;
const NavigationAnchorItemBase = forwardRef((props, ref) => {
  const {
    isMultiline,
    isMinimized,
    isCollapsing,
    isExpandingFadeOut,
    isExpandingFadeInPending,
    isExpandingFadeIn,
    isActive,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'a';
  /* When isExpandingFadeOut we use only isExpandingFadeOutCss for layout + fade-out; do NOT apply
   * isMinimizedLayoutCss or its fade-in animation would override the expand fade-out. */
  const isMinimizedLayout = isMinimized && !isCollapsing && !isExpandingFadeOut;
  return <Component ref={ref} {...restProps} className={classNames(navigationAnchorItemBaseCss, {
    [navigationAnchorItemBaseIsMultilineCss]: isMultiline,
    [navigationAnchorItemBaseIsMinimizedCss]: isMinimized,
    [navigationAnchorItemBaseIsCollapsingCss]: isCollapsing,
    [navigationAnchorItemBaseIsExpandingFadeOutCss]: isExpandingFadeOut,
    [navigationAnchorItemBaseIsExpandingFadeInPendingCss]: isExpandingFadeInPending,
    [navigationAnchorItemBaseIsExpandingFadeInCss]: isExpandingFadeIn,
    [navigationAnchorItemBaseIsActiveCss]: isActive,
    [navigationAnchorItemBaseIsMinimizedLayoutCss]: isMinimizedLayout
  }, props.className)} />;
});

/**
 * Nav link (icon + label) with collapse behavior.
 * Fades out at full width, then uses minimized layout when width collapses.
 * Uses COLLAPSE_FADEOUT_MS so isCollapsing is true only during the initial fade-out.
 * On expand: fade out in minimized layout, then fade in in expanded layout (reverse of collapse).
 */
export const NavigationAnchorItem = props => {
  const state = useLeftNavigationState();
  const {
    isExpanding,
    expandFadeOutDone,
    expandFadeInStarted
  } = useExpandPhaseContext();
  const ref = useRef(null);
  const isMultiline = useTextWidth(ref, 175);
  const isMinimized = state === 'minimized';
  const [isCollapsing, setIsCollapsing] = useState(false);
  const isExpandingFadeOut = isExpanding && !expandFadeOutDone;
  const isExpandingFadeInPending = isExpanding && expandFadeOutDone && !expandFadeInStarted;
  const isExpandingFadeIn = isExpanding && expandFadeOutDone && expandFadeInStarted;

  /* useLayoutEffect so we set isCollapsing true before paint; otherwise first paint shows minimized layout and no fade-out */
  useLayoutEffect(() => {
    if (!isMinimized) {
      setIsCollapsing(false);
      return undefined;
    }
    setIsCollapsing(true);
    const t = setTimeout(() => setIsCollapsing(false), COLLAPSE_FADEOUT_MS);
    return () => clearTimeout(t);
  }, [isMinimized]);
  return <NavigationAnchorItemBase ref={ref} {...props} isMultiline={isMultiline} isMinimized={isMinimized} isCollapsing={isCollapsing} isExpandingFadeOut={isExpandingFadeOut} isExpandingFadeInPending={isExpandingFadeInPending} isExpandingFadeIn={isExpandingFadeIn} />;
};
const navigationWrapperCss = "nf4gkwg";

/** Scrollable column that holds the main nav links (scheduling, contacts, etc.). */
export const NavigationWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(navigationWrapperCss, props.className)} />;
});
const topNavigationCss = "topx8k5";
export const TopNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(topNavigationCss, props.className)} />;
});
const bottomNavigationCss = "b1ixkcz9";
export const BottomNavigation = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bottomNavigationCss, props.className)} />;
});
const loaderContainerCss = "lvwzemk";
export const LoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(loaderContainerCss, props.className)} />;
});

require("./elements.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./elements.js");